/**
 * EmailCampaignPreview — V1.9.19
 *
 * Superadmin preview + send controls for the one-time Independence
 * Week broadcast. Displays the full HTML email in an isolated iframe
 * (via srcDoc so cookies still authenticate under the hood), plus:
 *   • A "Send test to my inbox" button that uses my own email
 *   • Recipient counter (candidates eligible)
 *   • Big red "Send to all N candidates" button — hidden behind a
 *     confirmation modal to prevent accidental blasts.
 */

import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { EnvelopeSimple, PaperPlaneTilt, Warning, ArrowClockwise } from "@phosphor-icons/react";
import api from "@/lib/api";

export default function EmailCampaignPreview() {
    const [html, setHtml] = useState("");
    const [audience, setAudience] = useState("candidate");
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [testing, setTesting] = useState(false);
    const [extraEmails, setExtraEmails] = useState("");
    const [confirming, setConfirming] = useState(false);
    const [sending, setSending] = useState(false);
    const [sendResult, setSendResult] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(`/admin/email-preview/independence-2026?audience=${audience}`, {
                responseType: "text",
                transformResponse: [(d) => d],
            });
            setHtml(r.data);
            const s = await api.get("/admin/campaigns/independence-2026/stats");
            setStats(s.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || "Failed to load preview.");
        } finally {
            setLoading(false);
        }
    }, [audience]);

    useEffect(() => {
        load();
    }, [load]);

    const audienceCount = (() => {
        const by = stats?.eligible_by_audience || {};
        if (audience === "candidate") return by.candidate ?? 0;
        if (audience === "candidate_pending") return by.candidate_pending ?? 0;
        if (audience === "client_spoc") return by.client_spoc ?? 0;
        return stats?.eligible || 0;
    })();
    const audienceLabel = audience === "candidate_pending"
        ? "reg-pending candidates"
        : audience === "client_spoc"
            ? "client SPOCs"
            : "candidates";

    const sendTest = async () => {
        setTesting(true);
        const extras = extraEmails
            .split(/[\s,;]+/)
            .map((e) => e.trim())
            .filter((e) => e && e.includes("@"));
        try {
            const r = await api.post("/admin/campaigns/independence-2026/test", {
                audience,
                extra_emails: extras,
            });
            const extraNote = r.data.extras_sent
                ? ` + ${r.data.extras_sent} extra`
                : audience === "client_spoc" && extras.length
                    ? ` (CC to ${extras.length} extra)`
                    : "";
            toast.success(`Test (${r.data.audience}) sent to ${r.data.to}${extraNote}. Check inbox.`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || "Test send failed.");
        } finally {
            setTesting(false);
        }
    };

    const sendAll = async () => {
        setSending(true);
        setSendResult(null);
        try {
            const r = await api.post(`/admin/campaigns/independence-2026/send-all?audience=${audience}`);
            setSendResult(r.data);
            toast.success(`Queued ${r.data.queued} emails. Delivery will complete in the background.`);
            setConfirming(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || "Bulk send failed.");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="p-6 max-w-6xl mx-auto" data-testid="email-campaign-preview-page">
            <div className="flex items-start justify-between gap-3 mb-6">
                <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-ink/40">
                        Growth · One-time broadcast
                    </p>
                    <h1 className="font-heading text-2xl font-black mt-1">
                        Independence Week Email · Preview &amp; Send
                    </h1>
                    <p className="text-sm text-ink/60 mt-1 max-w-2xl">
                        A one-shot campaign to invite every candidate to try DaddyTalk chat + our free tools this Independence Week.
                        Preview the email on the right, then send a test to yourself, then release to the full audience.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={load}
                    disabled={loading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-ink/20 text-xs font-black uppercase tracking-wider hover:border-cobalt hover:text-cobalt disabled:opacity-40 rounded-md"
                >
                    <ArrowClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} />
                    Refresh
                </button>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                {/* Actions column */}
                <div className="space-y-5">
                    <section className="bg-white border border-ink/10 rounded-md p-5" data-testid="campaign-stats">
                        <div className="flex items-center gap-2 mb-3">
                            <EnvelopeSimple size={16} weight="bold" className="text-cobalt" />
                            <h2 className="font-heading font-black">Audience</h2>
                        </div>
                        {stats ? (
                            <ul className="text-sm space-y-1.5">
                                <li className="flex justify-between border-b border-ink/5 pb-1.5">
                                    <span className="text-ink/60">Registered candidates</span>
                                    <span className="font-black">{stats.eligible_by_audience?.candidate ?? 0}</span>
                                </li>
                                <li className="flex justify-between border-b border-ink/5 pb-1.5">
                                    <span className="text-ink/60">Registration-pending</span>
                                    <span className="font-black">{stats.eligible_by_audience?.candidate_pending ?? 0}</span>
                                </li>
                                <li className="flex justify-between border-b border-ink/5 pb-1.5">
                                    <span className="text-ink/60">Client SPOCs</span>
                                    <span className="font-black">{stats.eligible_by_audience?.client_spoc ?? 0}</span>
                                </li>
                                <li className="flex justify-between border-b border-ink/5 pb-1.5">
                                    <span className="text-ink/60">Already sent</span>
                                    <span className="font-black">{stats.already_sent}</span>
                                </li>
                                <li className="flex justify-between pt-1 border-t border-ink/10">
                                    <span className="text-ink/80 font-bold">Total eligible</span>
                                    <span className="font-black text-cobalt">{stats.eligible}</span>
                                </li>
                            </ul>
                        ) : (
                            <p className="text-xs text-ink/50">Loading…</p>
                        )}
                    </section>

                    <section className="bg-white border border-ink/10 rounded-md p-5" data-testid="campaign-actions">
                        <h2 className="font-heading font-black mb-3">Send</h2>
                        <div className="mb-3">
                            <label className="block text-[10px] font-black uppercase tracking-wider text-ink/60 mb-1">
                                Extra email addresses (comma / newline separated) — optional
                            </label>
                            <textarea
                                data-testid="campaign-extra-emails"
                                value={extraEmails}
                                onChange={(e) => setExtraEmails(e.target.value)}
                                rows={2}
                                placeholder="hr@acme.com, spoc@bigco.in"
                                className="w-full px-3 py-2 border border-ink/20 text-xs rounded-md focus:outline-none focus:border-cobalt"
                            />
                            <p className="text-[10px] text-ink/50 mt-1">
                                {audience === "client_spoc"
                                    ? "Added as CC alongside swati@, dimple@, harish@."
                                    : "Sends the same email to each address (one-off)."}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={sendTest}
                            disabled={testing}
                            data-testid="campaign-send-test"
                            className="w-full inline-flex items-center justify-center gap-2 border border-cobalt text-cobalt hover:bg-cobalt hover:text-white px-4 py-2.5 text-xs font-black uppercase tracking-wider transition disabled:opacity-40 rounded-md mb-3"
                        >
                            <PaperPlaneTilt size={12} weight="bold" />
                            {testing ? "Sending…" : "Send test to my inbox"}
                        </button>
                        <button
                            type="button"
                            onClick={() => setConfirming(true)}
                            disabled={!stats || audienceCount === 0}
                            data-testid="campaign-send-all"
                            className="btn-gradient w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-xs font-black uppercase tracking-wider disabled:opacity-40 rounded-md"
                        >
                            <PaperPlaneTilt size={12} weight="bold" />
                            Send to all {audienceCount} {audienceLabel}
                        </button>
                        {sendResult && (
                            <div className="mt-4 border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs p-3 rounded-md">
                                Queued <b>{sendResult.queued}</b> emails. They'll deliver in the background over ~{Math.ceil(sendResult.queued / 50)} minutes.
                            </div>
                        )}
                    </section>
                </div>

                {/* Preview column */}
                <div className="bg-white border border-ink/10 rounded-md overflow-hidden" data-testid="campaign-preview-frame">
                    <div className="px-4 py-2 bg-cream/60 border-b border-ink/10 flex items-center justify-between gap-2">
                        <span className="text-[10px] font-black uppercase tracking-wider text-ink/60">Live preview</span>
                        <div className="flex items-center gap-1">
                            {[
                                { id: "candidate", label: "Candidate" },
                                { id: "candidate_pending", label: "Reg-pending" },
                                { id: "client_spoc", label: "Client SPOC" },
                            ].map((v) => (
                                <button
                                    key={v.id}
                                    type="button"
                                    onClick={() => setAudience(v.id)}
                                    data-testid={`campaign-audience-${v.id}`}
                                    className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded ${
                                        audience === v.id ? "bg-ink text-white" : "text-ink/50 hover:text-ink"
                                    }`}
                                >
                                    {v.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    {loading ? (
                        <p className="p-8 text-xs text-ink/50 text-center">Loading preview…</p>
                    ) : (
                        <iframe
                            title="Independence Email preview"
                            srcDoc={html}
                            className="w-full"
                            style={{ height: "820px", border: 0 }}
                        />
                    )}
                </div>
            </div>

            {confirming && (
                <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4"
                     onClick={() => !sending && setConfirming(false)}>
                    <div className="bg-white max-w-md w-full p-5 rounded-md" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2 mb-2">
                            <Warning size={18} weight="fill" className="text-red-600" />
                            <h3 className="font-heading font-black">Confirm bulk send</h3>
                        </div>
                        <p className="text-sm text-ink/70">
                            You're about to email <b>{audienceCount} {audienceLabel}</b>. This cannot
                            be undone. Delivery will be batched and complete over the next several
                            minutes. Have you sent a test to yourself and verified everything renders
                            correctly?
                        </p>
                        <div className="flex justify-end gap-2 mt-5">
                            <button
                                type="button"
                                onClick={() => setConfirming(false)}
                                disabled={sending}
                                className="px-4 py-2 text-xs font-black uppercase tracking-wider text-ink/60"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={sendAll}
                                disabled={sending}
                                data-testid="campaign-send-all-confirm"
                                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-xs font-black uppercase tracking-wider disabled:opacity-40 rounded-md"
                            >
                                {sending ? "Sending…" : "Yes, send to everyone"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
